import { createTimeInstance, setDateBack } from '@utils/time';

// A relative-or-explicit date window, and the one function that turns it into bounds.
//
// Lifted out of utils/documentVault.js when the score-spec list needed the same six presets for the
// same reason: narrowing a loaded list by when its rows were last touched. It lives here rather than
// there because documentVault.js pulls in file-saver and the whole @constants/documents doc-type
// table, and none of that belongs on an unrelated route.
//
// The state shape is `{ preset, from, to }`, where `from`/`to` are bare 'YYYY-MM-DD' strings and are
// meaningful only under the `custom` preset. Resolution is in LOCAL time, deliberately: every
// surface that filters on a timestamp also renders that timestamp locally, so the bound the operator
// sets and the date they read agree. A server instant at UTC midnight therefore belongs to the
// previous local day in both places.

export const DATE_RANGE_PRESETS = {
  all: 'all',
  last1: 'last1',
  last7: 'last7',
  last30: 'last30',
  qtd: 'qtd',
  ytd: 'ytd',
  custom: 'custom',
};

export const DATE_RANGE_PRESET_LABELS = {
  [DATE_RANGE_PRESETS.all]: 'Any time',
  [DATE_RANGE_PRESETS.last1]: 'Last day',
  [DATE_RANGE_PRESETS.last7]: 'Last 7 days',
  [DATE_RANGE_PRESETS.last30]: 'Last 30 days',
  [DATE_RANGE_PRESETS.qtd]: 'Quarter to date',
  [DATE_RANGE_PRESETS.ytd]: 'Year to date',
  [DATE_RANGE_PRESETS.custom]: 'Custom',
};

// A stored 'YYYY-MM-DD' string -> the Date a picker wants. `createTimeInstance(null)` returns null
// rather than a dayjs, so the instance is checked before it is used.
export const toDateValue = (value) => {
  if (!value) return null;
  const instance = createTimeInstance(value);
  return instance ? instance.toDate() : null;
};

// A picker's Date -> the 'YYYY-MM-DD' string the filter state stores.
export const toIsoDate = (date) => {
  if (!date) return null;
  return createTimeInstance(date).format('YYYY-MM-DD');
};

// `{ from, to }` as dayjs instances, either of which may be null for "unbounded on that side".
// An unknown preset resolves to no bounds rather than to nothing — a hand-typed or stale value
// must widen the view, never hide every row.
export function resolveDateRange(dateRange) {
  const preset = dateRange?.preset || DATE_RANGE_PRESETS.all;

  if (preset === DATE_RANGE_PRESETS.all) return { from: null, to: null };

  if (preset === DATE_RANGE_PRESETS.custom) {
    return {
      from: dateRange?.from ? createTimeInstance(dateRange.from).startOf('day') : null,
      to: dateRange?.to ? createTimeInstance(dateRange.to).endOf('day') : null,
    };
  }

  // Midnight YESTERDAY, not 24 hours ago, because that is what the rest of this family means:
  // last7 is `setDateBack(7).startOf('day')`, so it spans today plus the seven calendar days before
  // it. A rolling 24-hour window here would make "Last day" the one preset whose boundary moves
  // through the day and whose result changes between two glances at the same list.
  if (preset === DATE_RANGE_PRESETS.last1) return { from: setDateBack(1, 'day').startOf('day'), to: null };
  if (preset === DATE_RANGE_PRESETS.last7) return { from: setDateBack(7, 'day').startOf('day'), to: null };
  if (preset === DATE_RANGE_PRESETS.last30) return { from: setDateBack(30, 'day').startOf('day'), to: null };
  if (preset === DATE_RANGE_PRESETS.qtd) {
    // dayjs only understands startOf('quarter') with the quarterOfYear plugin, which this app
    // does not extend -- derive the quarter's first month instead.
    const now = createTimeInstance();
    const quarterStartMonth = Math.floor(now.month() / 3) * 3;
    return { from: now.month(quarterStartMonth).startOf('month'), to: null };
  }
  if (preset === DATE_RANGE_PRESETS.ytd) return { from: createTimeInstance().startOf('year'), to: null };

  return { from: null, to: null };
}

// Whether a resolved window actually narrows anything. `custom` with neither end set is a preset
// the operator has selected but not yet filled in, and it must not read as an active filter —
// otherwise an empty list blames a filter that is matching everything.
export const isBoundedRange = (resolvedRange) => !!(resolvedRange?.from || resolvedRange?.to);

// Whether a timestamp falls inside a resolved window. An unresolved window matches everything; a
// row with no timestamp at all is excluded only once a bound exists, mirroring matchesDateRange in
// utils/documentVault.js.
export const isWithinRange = (value, resolvedRange) => {
  const { from, to } = resolvedRange || {};
  if (!from && !to) return true;
  if (!value) return false;

  const instance = createTimeInstance(value);
  if (!instance || !instance.isValid()) return false;
  if (from && instance.isBefore(from)) return false;
  if (to && instance.isAfter(to)) return false;
  return true;
};
